import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ParapheurPage from './pages/ParapheurPage';
import AdminPage from './pages/AdminPage';
import NouvelleDemandePage from './pages/NouvelleDemandePage';
import DetailDemandePage from './pages/DetailDemandePage';
import PDGDashboardPage from './pages/PDGDashboardPage';
import ClassementPage from './pages/ClassementPage';
import ParapheurRolePage from './pages/ParapheurRolePage';
import NouveauDocumentPage from './pages/NouveauDocumentPage';
import DashboardDemandeur from './pages/DashboardDemandeur';
import ArchivesPage from './pages/ArchivesPage';
import DashboardActeur from './pages/DashboardActeur';
import ChangerMotDePassePage from './pages/ChangerMotDePassePage';
import ExpirationSession from './components/ExpirationSession';
import NavigationMobile from './components/NavigationMobile';
import EtatReseau from './components/EtatReseau';









import Bouclier from './components/Bouclier';
import DelegationsPage from './pages/DelegationsPage';
// Recharts pese lourd et ne sert qu'ici : la page est chargee a la demande,
// pour ne pas alourdir le premier rendu de tous les autres ecrans.
const AnalyticsDashboard = React.lazy(() => import('./pages/AnalyticsDashboard'));
// Tant que /auth/me n'a pas repondu, `user` est null : seul le jeton survit au
// rechargement. Trancher les droits a cet instant revient a les evaluer sur une
// information absente — c'est ce qui renvoyait le PDG vers /dashboard a chaque
// rafraichissement de /parapheur. On attend donc le profil avant de decider.
const Attente = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#8899AA', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13 }}>
    Chargement...
  </div>
);

// Route privee — doit etre connecte
// Un compte dont le mot de passe a ete reinitialise par un administrateur ne
// peut atteindre aucun ecran tant qu'il ne l'a pas change.
const PrivateRoute = ({ children }) => {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  // Attendre le profil sert deux fins : aucune page ne s'affiche avec un `user`
  // nul — d'ou provenaient les plantages au rafraichissement —, et un compte
  // tenu de changer son mot de passe ne peut plus entrevoir un ecran pendant le
  // temps de reponse de /auth/me.
  if (!user) return <Attente />;
  if (user.changer_mot_de_passe_requis) return <Navigate to="/changer-mot-de-passe" replace />;
  return children;
};

// Accessible avec un simple jeton : la placer derriere PrivateRoute
// declencherait une boucle de redirection avec le controle ci-dessus.
const ChangerMotDePasseRoute = ({ children }) => {
  const token = useAuthStore(s => s.token);
  return token ? children : <Navigate to="/login" replace />;
};

// Route PDG uniquement
const PDGRoute = ({ children }) => {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (!user) return <Attente />;
  if (user?.changer_mot_de_passe_requis) return <Navigate to="/changer-mot-de-passe" replace />;
  const isPDG = user?.roles?.some(r => r.role === 'pdg');
  if (!isPDG) return <Navigate to="/dashboard" replace />;
  return children;
};


const AdminRoute = ({ children }) => {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (!user) return <Attente />;
  const isAdmin = user?.roles?.some(r => r.role === 'admin') || user?.isGlobal;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

function App({ enregistrementSW }) {
  const { token, fetchMe } = useAuthStore();

  useEffect(() => {
    if (token) fetchMe();
  }, [token, fetchMe]);

  return (
    <Bouclier>
    <BrowserRouter>
      {/* Surveillance de l'inactivite : active des qu'un jeton existe */}
      <ExpirationSession />
      {/* Barre basse et bandeaux d'etat : ne s'affichent qu'en mobile / hors ligne */}
      <NavigationMobile />
      <EtatReseau enregistrementSW={enregistrementSW} />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route path="/dashboard" element={
          <PrivateRoute><DashboardPage /></PrivateRoute>
        } />

        <Route path="/parapheur" element={
          <PDGRoute><ParapheurPage /></PDGRoute>
        } />

        <Route path="/admin" element={
          <AdminRoute><AdminPage /></AdminRoute>
        } />

        <Route path="/" element={
          <Navigate to={token ? "/dashboard" : "/login"} replace />
        } />
        <Route path="/nouvelle-demande" element={
          <PrivateRoute><NouvelleDemandePage /></PrivateRoute>
        } />
        <Route path="/demandes/:id" element={
          <PrivateRoute><DetailDemandePage /></PrivateRoute>
        } />
        <Route path="/pdg/dashboard" element={
          <PDGRoute><PDGDashboardPage /></PDGRoute>
        } />
        <Route path="/changer-mot-de-passe" element={
          <ChangerMotDePasseRoute><ChangerMotDePassePage /></ChangerMotDePasseRoute>
        } />
        <Route path="/classement" element={
          <PrivateRoute><ClassementPage /></PrivateRoute>
        } />
        <Route path="/parapheur-role" element={
          <PrivateRoute><ParapheurRolePage /></PrivateRoute>
        } />
        <Route path="/nouveau-document" element={
          <PrivateRoute><NouveauDocumentPage /></PrivateRoute>
        } />
        <Route path="/archives" element={<PrivateRoute><ArchivesPage /></PrivateRoute>} />
        <Route path="/dashboard-demandeur" element={
          <PrivateRoute><DashboardDemandeur /></PrivateRoute>
        } />
        <Route path="/dashboard-acteur" element={
          <PrivateRoute><DashboardActeur /></PrivateRoute>
        } />
        <Route path="/delegations" element={
          <PrivateRoute><DelegationsPage /></PrivateRoute>
        } />
        <Route path="/analyses" element={
          <PrivateRoute>
            <React.Suspense fallback={<Attente />}><AnalyticsDashboard /></React.Suspense>
          </PrivateRoute>
        } />
        {/* Sans route attrape-tout, une URL inconnue ne correspond a rien et
            React Router ne rend rien du tout : ecran blanc, sans erreur. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>



    </BrowserRouter>
    </Bouclier>
  );
}

export default App;
